import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLoad } from "@/lib/hooks";
import { fmt, fdate } from "@/lib/api";
import { useAccess } from "@/context/AuthContext";
import { PageHeader, StatusBadge, EmptyRow, Section, StatCard } from "@/components/common";
import { PageSearch } from "@/components/PageSearch";
import { InvoiceForm } from "./InvoiceForm";

function Summary({ cur }) {
  const [d] = useLoad("/tenant/dashboard");
  if (!d) return null;
  const co = d.counter_outstanding || { amount: 0, count: 0 };
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="invoices-summary">
      <StatCard testId="inv-sum-invoiced" label="Invoiced" value={fmt(d.invoiced, cur)} />
      <StatCard testId="inv-sum-outstanding" tone="warning" label="Outstanding on invoices" value={fmt(d.outstanding, cur)} hint={`${(d.counts.unpaid || 0) + (d.counts.part_paid || 0)} open`} />
      <StatCard testId="inv-sum-counter" tone="warning" label="Counter-sale balances" value={fmt(co.amount, cur)} hint={`${co.count} walk-in sale${co.count === 1 ? "" : "s"} not fully paid (no invoice)`} />
      <StatCard testId="inv-sum-total" label="Total receivable" value={fmt(d.outstanding + co.amount, cur)} hint="Invoices + counter-sale balances" />
    </div>
  );
}

const FILTERS = [["", "All"], ["unpaid", "Unpaid"], ["part_paid", "Part-paid"], ["paid", "Paid"], ["cancelled", "Cancelled"]];

export default function Invoices() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "";
  const [rows] = useLoad(`/tenant/invoices${status ? `?status=${status}` : ""}`);
  const [open, setOpen] = useState(false);
  const { canWrite, currency } = useAccess();
  const navigate = useNavigate();

  return (
    <>
      <PageHeader title="Invoices" subtitle="Totals lock automatically once a payment is recorded.">
        <PageSearch testId="invoices-search" placeholder="Search invoice number or customer" />
        {canWrite(["tenant_admin", "accountant", "sales"]) && (
          <Button data-testid="new-invoice-button" onClick={() => setOpen(true)} className="bg-[#45BFB4] text-[#13294B] hover:bg-[#389F95] hover:text-white"><Plus className="mr-1 h-4 w-4" />New invoice</Button>
        )}
      </PageHeader>
      <Summary cur={currency} />
      <div className="flex flex-wrap gap-2">
        {FILTERS.map(([v, l]) => (
          <button key={v} data-testid={`invoice-filter-${v || "all"}`} onClick={() => setParams(v ? { status: v } : {})}
            className={`rounded-full border px-3 py-1 text-sm transition-colors ${status === v ? "border-[#13294B] bg-[#13294B] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>{l}</button>
        ))}
      </div>
      <Section>
        <div className="overflow-x-auto">
          <table className="po-table" data-testid="invoices-table">
            <thead><tr><th>Number</th><th>Customer</th><th>Issued</th><th>Due</th><th>Status</th><th className="po-num">Total</th><th className="po-num">Paid</th><th className="po-num">Balance</th></tr></thead>
            <tbody>
              {rows && rows.length === 0 && <EmptyRow cols={8} text="No invoices match this filter." />}
              {rows?.map((i) => (
                <tr key={i.id} className="cursor-pointer" data-testid={`invoice-row-${i.number}`} onClick={() => navigate(`/app/invoices/${i.id}`)}>
                  <td className="font-medium text-[#13294B]"><Link to={`/app/invoices/${i.id}`}>{i.number}</Link>{i.locked && <Lock className="ml-1.5 inline h-3.5 w-3.5 text-slate-400" />}</td>
                  <td>{i.customer_name}</td>
                  <td>{fdate(i.created_at)}</td>
                  <td>{fdate(i.due_date)}</td>
                  <td><StatusBadge status={i.status} /></td>
                  <td className="po-num">{fmt(i.total, i.currency)}</td>
                  <td className="po-num">{fmt(i.amount_paid, i.currency)}</td>
                  <td className="po-num font-medium">{i.status === "cancelled" ? "—" : fmt(i.total - i.amount_paid - (i.amount_credited || 0), i.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
      <InvoiceForm open={open} onOpenChange={setOpen} currency={currency} onSaved={(inv) => navigate(`/app/invoices/${inv.id}`)} />
    </>
  );
}
